/* eslint-disable jsx-a11y/no-autofocus */
import React, { useState, useEffect } from 'react';
import { useCart } from 'react-use-cart';
import { useForm, useFieldArray } from 'react-hook-form';
import { Icon } from '@iconify/react';
import Cart from '@iconify/icons-mdi/cart';
import useLocalStorage from 'use-local-storage-state';

import trackEvent from '~/hooks/useEventTracker';

import Button from '~/components/modules/buttons/default';

const OrderableItemsForm = ({ item, cartButtonText, onComplete }) => {
	const { addItem, getItem, removeItem, updateItemQuantity, setItems, inCart } =
		useCart();

	const availabilities = item.Availability.filter(
		orderableItem =>
			orderableItem.data.Status !== 'Hidden' &&
			orderableItem.data.Orderable === true
	);

	// sort availabilities by label and then by availability
	availabilities.sort((a, b) => {
		if (a.data.Label && b.data.Label) {
			return a.data.Label.localeCompare(b.data.Label);
		} else if (a.data.Label) {
			return -1; // a has label, b does not
		} else if (b.data.Label) {
			return 1; // b has label, a does not
		} else {
			return a.data.Availability.localeCompare(b.data.Availability);
		}
	});

	const itemsInCart = availabilities.some(availability =>
		inCart(availability.recordId)
	);

	const [quantities, setQuantities] = useState(
		new Array(availabilities.length).fill('')
	);

	const [cartStorage] = useLocalStorage('react-use-cart', null);

	useEffect(() => {
		if (cartStorage?.items) {
			setItems(cartStorage.items);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cartStorage]);

	useEffect(() => {
		setQuantities(quantities =>
			quantities.map(
				(quantity, index) =>
					getItem(availabilities[index].recordId)?.quantity || quantity
			)
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { control, register, handleSubmit, reset } = useForm({
		mode: 'onSubmit'
	});

	useFieldArray({
		control,
		name: 'item'
	});

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isComplete, setIsComplete] = useState(false);

	const onSubmit = values => {
		let somethingWasOrdered = false;
		setIsSubmitting(true);
		values.item.forEach(itemValues => {
			if (itemValues.quantity) {
				const quantity = parseInt(itemValues.quantity);

				if (quantity > 0) {
					somethingWasOrdered = true;
					if (inCart(itemValues.id)) {
						updateItemQuantity(itemValues.id, parseInt(quantity));
						trackEvent('Cart', 'Update Quantity', itemValues.sku, quantity);
					} else {
						addItem(
							{
								id: itemValues.id,
								availability: itemValues.availability,
								sku: itemValues.sku,
								item: item,
								price: 0
							},
							parseInt(quantity)
						);
						trackEvent('Cart', 'Add Item', itemValues.sku, quantity);
					}
				} else if (quantity === 0 && inCart(itemValues.id)) {
					removeItem(itemValues.id);
					trackEvent('Cart', 'Remove Item', itemValues.sku);
				}
			}
		});
		reset();
		if (somethingWasOrdered) {
			setIsComplete(true);
			trackEvent('Cart', 'Item(s) Updated', values.item.length);
		}
		setIsSubmitting(false);
		if (onComplete) {
			onComplete();
		}
	};

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<div className="p-3 space-y-1 max-h-48 sm:max-h-36 overflow-y-scroll">
				<p className="text-2xs mb-2">
					Enter requested{' '}
					{availabilities.length === 1 ? 'quantity' : 'quantities'}:
				</p>
				{availabilities.map((availability, index) => (
					<div key={availability.recordId}>
						<div
							className={`
								flex items-center gap-1
								${availability.data.Status === 'Out of Stock' ? 'opacity-50' : ''}
							`}
						>
							<div className="order-last w-9">
								<input
									id={availability.recordId}
									{...register(`item.${index}.quantity`)}
									type="tel"
									autoComplete="off"
									autoFocus={index === 0 && !itemsInCart ? true : false}
									className="w-full text-center border-gray-400 text-xs px-1 py-0.5 placeholder:text-gray-300 placeholder:text-2xs"
									placeholder="Qty"
									aria-required="false"
									disabled={
										isSubmitting || availability.data.Status === 'Out of Stock'
									}
									defaultValue={quantities[index]}
									onFocus={e =>
										e.target.setSelectionRange(0, e.target.value.length)
									}
								/>
								<input
									type="hidden"
									{...register(`item.${index}.id`)}
									value={availability.recordId}
								/>
								<input
									type="hidden"
									{...register(`item.${index}.sku`)}
									value={availability.data.SKU}
								/>
								<input
									type="hidden"
									{...register(`item.${index}.availability`)}
									value={availability.data.Availability}
								/>
							</div>
							<label
								className={`flex-1 order-first text-2xs text-right leading-tight ${availability.data.Label ? 'tracking-tight' : ''}`}
								htmlFor={availability.recordId}
							>
								{availability.data.Label && (
									<span className="text-2xs font-bold">
										{' '}
										{availability.data.Label},{' '}
									</span>
								)}
								<span
									className={
										availability.data.Label ? 'font-normal' : 'font-bold'
									}
								>
									{availability.data.Language[0].data.Name}
								</span>
								{availability.data.Status === 'Out of Stock' ? (
									<span className="font-bold text-red-700 block">
										{' '}
										(Out of stock)
									</span>
								) : null}
								{availability.data.Limit && (
									<span className="text-2xs font-normal text-gray-500 pl-1">
										(Limit: {availability.data.Limit})
									</span>
								)}
							</label>
						</div>
					</div>
				))}
			</div>

			<Button
				as="button"
				type="submit"
				variant="secondary"
				size="sm"
				className="w-full flex items-center justify-between !text-2xs"
			>
				{isComplete
					? 'Cart updated!'
					: isSubmitting
						? 'Adding...'
						: `${
								cartButtonText
									? cartButtonText
									: itemsInCart
										? 'Update cart'
										: 'Add to cart'
							}`}
				<Icon icon={Cart} height="1rem" width="1rem" className="w-3 h-3" />
			</Button>
		</form>
	);
};

export default OrderableItemsForm;
